import type { License } from '../license/types.js';
import type { FeatureFlag, Tier } from '../schema/features.js';
import type { MobiLauncherConfig } from '../schema/index.js';
import type { Mdm } from '../schema/mdm.js';
import type { Profile } from '../schema/profile.js';
import type { Tile } from '../schema/tiles.js';
import { resolveFeatures } from '../tier/resolve.js';

import { type AgentHost, dispatchToAgent, type TileActivationResult } from './agent.js';
import type { AuditLog } from './features/audit.js';
import { collectHealthSnapshot, HeartbeatReporter } from './features/heartbeat.js';
import type { RemoteConfigEvent } from './features/remote-config.js';
import type { TelemetryEmitter } from './features/telemetry.js';
import { defaultFetcher, type Fetcher } from './http.js';
import { OemOrchestrator, type OemEvent } from './oem/orchestrator.js';
import type { OemId, OemModuleLoader } from './oem/types.js';
import type { RenderTarget } from './render-target.js';
import { resolveActiveProfile } from './schedule.js';
import { type Clock, SYSTEM_CLOCK, SYSTEM_TICKER, type Ticker } from './timing.js';

const SCHEDULE_TICK_INTERVAL_MS = 60_000;

export interface CreateEngineInput {
  config: MobiLauncherConfig;
  /** `null` = unlicensed → run as Core. */
  license: License | null;
  renderTarget: RenderTarget;
  agentHost: AgentHost;
  clock?: Clock;
  ticker?: Ticker;
  /**
   * Loader for OEM extension modules. Omit for non-OEM deployments and for
   * Phase 1 (no concrete OEM modules ship until 4.5+). Tests pass a fake.
   */
  oemModuleLoader?: OemModuleLoader;
  /**
   * HTTP client used by remote-config and telemetry. Defaults to
   * `globalThis.fetch`-based implementation; tests pass a fake.
   */
  fetcher?: Fetcher;
  /**
   * Resolved secret values keyed by reference (e.g. `secret://acme/telemetry`).
   * The build pipeline produces this map by looking up `authTokenRef` against
   * a secrets store; the runtime never sees raw tokens in `config.json`.
   */
  secrets?: Record<string, string>;
  /** Forwarded to the OEM orchestrator; tests use this to assert behavior. */
  onOemEvent?: (event: OemEvent) => void;
  /** Forwarded to the remote-config poller. */
  onRemoteConfigEvent?: (event: RemoteConfigEvent) => void;
  /**
   * Fired after a remote-config poll successfully swaps in a new config (i.e.
   * not on a rejected/rolled-back swap). The engine re-renders the active
   * profile itself; this lets the renderer sync state it derives from the raw
   * config outside the render path — signage-rotation source, the Android
   * lock-task package allowlist — so a remote edit takes full effect without a
   * reboot.
   */
  onConfigChanged?: (config: MobiLauncherConfig) => void;
  /**
   * Stable device identifier emitted on every telemetry event's
   * `attrs.deviceId`. Lets the Portal's Devices panel group events by
   * device. When omitted, no per-device grouping is possible — the
   * server-side ingest still accepts events, they just don't tie back
   * to a specific row in the registered-devices table.
   */
  deviceId?: string;
  /**
   * Full device-health heartbeat endpoint URL (e.g.
   * `https://api.mobilauncher.com/devices/heartbeat`). When present AND
   * `deviceId` is set, the engine runs a periodic health heartbeat on ALL
   * tiers (not gated on `telemetryExport`) — fleet liveness for every device.
   * Omit to disable (tests, or hosts without an API base).
   */
  heartbeatEndpoint?: string;
  /** Launcher build string, reported in the heartbeat snapshot. */
  appVersion?: string;
  /**
   * License status the renderer resolved at boot — 'valid' | 'invalid' |
   * 'none'. Reported in the heartbeat so the fleet view can catch a device
   * that silently fell back to Core (a present-but-rejected license). Distinct
   * from `license` being null: the engine only sees the applied License, not
   * whether a license text was present-but-rejected.
   */
  licenseStatus?: string;
  /**
   * The id of the license the device is running on — reported in the heartbeat
   * so a device flagged `licenseStatus: invalid` links straight to its license
   * row. The engine's `license` may be null (rejected) even when a license id
   * existed, so the renderer passes it explicitly.
   */
  licenseId?: string;
}

export interface Engine {
  /**
   * Effective tier the runtime is honoring. Falls back to `'core'` whenever
   * the license is null (missing, expired, tampered, tenant mismatch).
   * Stable for the engine's lifetime — license is fixed at boot.
   */
  readonly tier: Tier;
  /** Effective feature set; recomputed on remote-config swap. */
  readonly features: ReadonlySet<FeatureFlag>;
  /** Currently-active OEM (if any). */
  readonly activeOem: OemId | null;
  /** Telemetry emitter; non-null when entitled and configured. */
  readonly telemetry: TelemetryEmitter | null;
  /** Audit log; non-null when entitled and `auditLogEnabled` in config. */
  readonly audit: AuditLog | null;
  /**
   * The host abstraction the engine dispatches tile activations through.
   * Exposed so renderer code (e.g., the diagnostic / device-info modal)
   * can feature-detect optional capabilities like `getDeviceProperties`
   * without re-plumbing the host through every component.
   */
  readonly agentHost: AgentHost;
  /**
   * MDM platform owning this deployment, mirrored from `config.mdm`.
   * Drives Device Info dialog field visibility. Stable for the engine's
   * lifetime — a remote-config swap that changes mdm is a re-mount, not
   * a live update.
   */
  readonly mdm: Mdm;

  start(): Promise<void>;
  stop(): Promise<void>;
  dispatchTile(tile: Tile): Promise<TileActivationResult>;
  /** Re-evaluate the active profile against the current clock; re-render if changed. */
  refreshActiveProfile(): void;
}

export function createEngine(input: CreateEngineInput): Engine {
  const clock = input.clock ?? SYSTEM_CLOCK;
  const ticker = input.ticker ?? SYSTEM_TICKER;
  const fetcher = input.fetcher ?? defaultFetcher;
  const oem = new OemOrchestrator();

  // Mutable state — swapped by remote-config polls.
  let liveConfig = input.config;
  let features = resolveFeatures(liveConfig, input.license);
  // Effective tier — fixed at boot (license doesn't change mid-session).
  const tier: Tier = input.license?.tier ?? 'core';

  let started = false;
  let stopped = false;
  let cancelScheduleTicker: (() => void) | null = null;
  let activeProfileId: string | null = null;
  // Signature of what was last handed to the render target — the active
  // profile's content plus the entitled feature set. Repaint decisions compare
  // against this rather than the profile *id*, so a remote-config edit to the
  // currently-active profile (same id, new tiles/theme) or a change to the
  // entitled features actually re-renders, while an unchanged poll does not
  // (no flicker on a kiosk).
  let renderedSignature: string | null = null;

  let telemetry: TelemetryEmitter | null = null;
  let audit: AuditLog | null = null;
  let remoteConfigStop: (() => void) | null = null;
  let heartbeat: HeartbeatReporter | null = null;
  // Latest remote-config etag, surfaced in the heartbeat as configVersion so
  // the fleet view can spot devices lagging on an old config.
  let latestConfigEtag: string | null = null;
  // Wall-clock at engine construction, for the heartbeat's uptime signal.
  const startMs = clock.now().getTime();

  function pickActiveProfile(): Profile {
    return resolveActiveProfile({
      config: liveConfig,
      now: clock.now(),
      scheduledProfilesEntitled: features.has('scheduledProfiles'),
    });
  }

  // Captures everything renderProfile consumes: the active profile's content
  // and the entitled feature set (order-normalized). Change to either → repaint.
  function renderSignature(profile: Profile): string {
    return JSON.stringify({ profile, features: [...features].sort() });
  }

  function emitRender(profile: Profile): void {
    const previousId = activeProfileId;
    activeProfileId = profile.id;
    renderedSignature = renderSignature(profile);
    input.renderTarget.renderProfile({ profile, features });
    if (previousId !== null && previousId !== profile.id) {
      const previousProfile = liveConfig.profiles.find((p) => p.id === previousId);
      telemetry?.enqueue({
        category: 'profileSwitch',
        name: 'changed',
        attrs: {
          fromId: previousId,
          toId: profile.id,
          ...(previousProfile !== undefined ? { fromName: previousProfile.name } : {}),
          toName: profile.name,
        },
      });
    }
  }

  function refreshActiveProfile(): void {
    if (!started || stopped) return;
    const next = pickActiveProfile();
    if (renderSignature(next) !== renderedSignature) emitRender(next);
  }

  function handleOemEvent(event: OemEvent): void {
    input.onOemEvent?.(event);
    telemetry?.enqueue({
      category: 'oem',
      name: event.type,
      attrs: 'oem' in event ? { oem: event.oem } : {},
    });
    // Surface OEM lifecycle to the MDM server log via the agent.
    const oemSuffix = 'oem' in event ? ` (${event.oem})` : '';
    if (event.type === 'applied') {
      input.agentHost.log?.('info', `OEM module applied${oemSuffix}`);
    } else if (
      event.type === 'loadFailed' ||
      event.type === 'detectFailed' ||
      event.type === 'applyFailed'
    ) {
      input.agentHost.log?.('error', `OEM ${event.type}${oemSuffix}: ${event.error.message}`);
    }
  }

  function handleRemoteConfigEvent(event: RemoteConfigEvent): void {
    input.onRemoteConfigEvent?.(event);
    if (event.type === 'pollSucceeded' && event.etag) {
      latestConfigEtag = event.etag;
    }
    telemetry?.enqueue({
      category: 'remoteConfigPoll',
      name: event.type,
      attrs: eventAttrs(event),
    });
    if (event.type === 'pollParseError' || event.type === 'pollServerError') {
      telemetry?.enqueue({
        category: 'configError',
        name: event.type,
        attrs: eventAttrs(event),
      });
    }
  }

  // Read `stopped` through a function so the type-checker doesn't narrow it to
  // its last synchronous assignment across an await — stop() can flip it while
  // start() is suspended, which flow analysis alone doesn't model.
  const isStopped = (): boolean => stopped;

  /**
   * Tear down every started subsystem. Each primitive is idempotent (guards on
   * its own handle), so calling this more than once — e.g. from stop() and
   * again from a start() that discovers it was stopped mid-load — is safe.
   */
  async function teardownSubsystems(): Promise<void> {
    if (cancelScheduleTicker) {
      cancelScheduleTicker();
      cancelScheduleTicker = null;
    }
    if (remoteConfigStop) {
      remoteConfigStop();
      remoteConfigStop = null;
    }
    if (telemetry) {
      await telemetry.stop();
    }
    if (heartbeat) {
      heartbeat.stop();
      heartbeat = null;
    }
    await oem.dispose();
  }

  /**
   * Start the device-health heartbeat (all tiers) when an endpoint + deviceId
   * are configured. Not part of loadEnterpriseFeatures — it's licence-agnostic
   * fleet liveness, so it runs for Core devices too.
   */
  function startHeartbeat(): void {
    if (heartbeat || !input.heartbeatEndpoint || input.deviceId === undefined) return;
    const deviceId = input.deviceId;
    heartbeat = new HeartbeatReporter({
      endpoint: input.heartbeatEndpoint,
      deviceId,
      tenantId: liveConfig.tenantId,
      fetcher,
      ticker,
      clock,
      collect: () =>
        collectHealthSnapshot(input.agentHost, {
          tier: () => tier,
          uptimeSec: () => Math.floor((clock.now().getTime() - startMs) / 1000),
          appVersion: input.appVersion ?? null,
          configVersion: () => latestConfigEtag,
          licenseStatus: input.licenseStatus ?? null,
          licenseId: input.licenseId ?? null,
        }),
    });
    heartbeat.start();
  }

  async function loadEnterpriseFeatures(): Promise<void> {
    const tasks: Promise<void>[] = [];

    if (features.has('auditLog') && liveConfig.adminAccess.auditLogEnabled) {
      tasks.push(
        import('./features/audit.js').then((mod) => {
          audit = new mod.AuditLog({
            clock,
            sink: (entry) => {
              telemetry?.enqueue({
                category: 'audit',
                name: entry.action,
                attrs: {
                  at: entry.at,
                  ...(entry.details !== undefined ? { details: entry.details } : {}),
                },
              });
            },
          });
        }),
      );
    }

    if (
      features.has('telemetryExport') &&
      liveConfig.telemetry?.enabled &&
      liveConfig.telemetry.endpoint
    ) {
      const telemetryConfig = liveConfig.telemetry;
      const endpoint = telemetryConfig.endpoint;
      if (endpoint !== undefined) {
        const authToken =
          telemetryConfig.authTokenRef !== undefined
            ? input.secrets?.[telemetryConfig.authTokenRef]
            : undefined;
        tasks.push(
          import('./features/telemetry.js').then((mod) => {
            telemetry = new mod.TelemetryEmitter({
              endpoint,
              ...(authToken !== undefined ? { authToken } : {}),
              enabledCategories: new Set(telemetryConfig.events),
              fetcher,
              ticker,
              clock,
              ...(input.deviceId !== undefined
                ? { defaultAttrs: { deviceId: input.deviceId } }
                : {}),
            });
            telemetry.start();
          }),
        );
      }
    }

    if (
      features.has('remoteConfig') &&
      liveConfig.remoteConfig?.enabled &&
      liveConfig.remoteConfig.endpoint
    ) {
      const rc = liveConfig.remoteConfig;
      const endpoint = rc.endpoint;
      if (endpoint !== undefined) {
        tasks.push(
          import('./features/remote-config.js').then((mod) => {
            const poller = new mod.RemoteConfigPoller({
              endpoint,
              pollIntervalSeconds: rc.pollIntervalSeconds,
              useETag: rc.useETag,
              fetcher,
              ticker,
              onConfig: (next) => {
                // Apply atomically: if the swapped-in config can't resolve an
                // active profile (e.g. a bad defaultProfileId), rolling back
                // keeps the launcher on the last good config instead of leaving
                // liveConfig/features half-updated.
                const prevConfig = liveConfig;
                const prevFeatures = features;
                liveConfig = next;
                features = resolveFeatures(liveConfig, input.license);
                try {
                  refreshActiveProfile();
                  // Only after the swap is known-good (refresh didn't throw)
                  // do we notify the renderer to sync its config-derived state.
                  input.onConfigChanged?.(liveConfig);
                } catch (err) {
                  liveConfig = prevConfig;
                  features = prevFeatures;
                  input.agentHost.log?.(
                    'error',
                    `remote config rejected: ${err instanceof Error ? err.message : String(err)}`,
                  );
                }
              },
              onEvent: handleRemoteConfigEvent,
            });
            poller.start();
            remoteConfigStop = () => {
              poller.stop();
            };
          }),
        );
      }
    }

    await Promise.all(tasks);
  }

  return {
    tier,
    mdm: liveConfig.mdm,
    agentHost: input.agentHost,
    get features() {
      return features;
    },
    get activeOem() {
      return oem.activeOem;
    },
    get telemetry() {
      return telemetry;
    },
    get audit() {
      return audit;
    },

    async start() {
      if (started) return;
      started = true;
      stopped = false;

      // emitRender on boot: previousId is null so no profileSwitch telemetry
      // fires, but it seeds activeProfileId + renderedSignature consistently.
      emitRender(pickActiveProfile());

      cancelScheduleTicker = ticker.start(refreshActiveProfile, SCHEDULE_TICK_INTERVAL_MS);

      // Fleet-liveness heartbeat — synchronous, all tiers, before the async
      // Enterprise load so a device reports in even if that load is slow.
      startHeartbeat();

      // Enterprise features must be loaded before OEM so OEM events have a
      // telemetry sink to emit into.
      await loadEnterpriseFeatures();
      // stop() may have raced us during the async subsystem load. If so it
      // found telemetry/poller still null and tore down nothing — so tear down
      // whatever we just started (which is idempotent) and bail before OEM.
      if (isStopped()) {
        await teardownSubsystems();
        return;
      }

      if (input.oemModuleLoader) {
        await oem.start({
          features,
          device: liveConfig.device,
          loadModule: input.oemModuleLoader,
          onEvent: handleOemEvent,
        });
        if (isStopped()) {
          await teardownSubsystems();
          return;
        }
      }
    },

    async stop() {
      if (stopped) return;
      stopped = true;
      await teardownSubsystems();
    },

    async dispatchTile(tile: Tile): Promise<TileActivationResult> {
      // Non-activatable tiles: spacers/dividers are decorative and folders are
      // navigational (the renderer opens a modal, never routes them here). If
      // one slips through, report it as unsupported — NOT as an agentError,
      // which would warn-log a UI routing mistake to the fleet admin as if the
      // device agent had failed.
      if (tile.type === 'spacer' || tile.type === 'divider' || tile.type === 'folder') {
        return { ok: false, reason: 'unsupportedTileType', type: tile.type };
      }
      if (tile.type === 'agentAction' && !features.has('agentActions')) {
        return { ok: false, reason: 'notEntitled', flag: 'agentActions' };
      }
      try {
        await dispatchToAgent(input.agentHost, tile);
        const result: TileActivationResult = { ok: true };
        emitTileTelemetry(tile, result);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const result: TileActivationResult = { ok: false, reason: 'agentError', message };
        // Forward the failure to the MDM server log so a fleet admin sees
        // it without needing the on-device telemetry/audit surfaces.
        input.agentHost.log?.('warn', `tile action failed: ${message}`);
        emitTileTelemetry(tile, result);
        return result;
      }
    },

    refreshActiveProfile,
  };

  function emitTileTelemetry(tile: Tile, result: TileActivationResult): void {
    if (!telemetry) return;
    if (tile.type === 'agentAction') {
      telemetry.enqueue({
        category: 'agentAction',
        name: tile.action,
        attrs: { ...tileDescriptor(tile), ok: result.ok, ...failureAttrs(result) },
      });
    } else {
      telemetry.enqueue({
        category: 'tileLaunch',
        name: tile.type,
        attrs: { ...tileDescriptor(tile), ok: result.ok, ...failureAttrs(result) },
      });
    }
  }
}

/**
 * Identifying fields for a tile, used as the base of every tile-related
 * telemetry / audit event. Always carries `tileId` (so a consumer can
 * cross-reference back to the config) plus the type-specific target so
 * the JSON is readable on its own — "which tile?" / "which app?" was
 * the question the raw UUID couldn't answer.
 *
 * Spacer and divider never reach this path (the dispatcher refuses
 * them earlier), so the union narrows to app / web / agentAction.
 */
function tileDescriptor(tile: Tile): Record<string, unknown> {
  if (tile.type === 'app') {
    return {
      tileId: tile.id,
      type: 'app',
      label: tile.label,
      packageName: tile.packageName,
    };
  }
  if (tile.type === 'web') {
    return {
      tileId: tile.id,
      type: 'web',
      label: tile.label,
      url: tile.url,
    };
  }
  if (tile.type === 'agentAction') {
    return {
      tileId: tile.id,
      type: 'agentAction',
      label: tile.label,
      action: tile.action,
      ...(tile.targetPackage !== undefined ? { targetPackage: tile.targetPackage } : {}),
    };
  }
  // spacer / divider / folder — dispatcher refuses these before they
  // ever reach a telemetry emitter, so a bare tileId is enough to
  // diagnose if one ever does slip through.
  return { tileId: tile.id, type: tile.type };
}

function failureAttrs(result: TileActivationResult): Record<string, unknown> {
  if (result.ok) return {};
  if (result.reason === 'agentError') return { reason: result.reason, message: result.message };
  if (result.reason === 'notEntitled') return { reason: result.reason, flag: result.flag };
  return { reason: result.reason, type: result.type };
}

function eventAttrs(event: RemoteConfigEvent): Record<string, unknown> {
  switch (event.type) {
    case 'pollSucceeded':
      return { status: event.status, changed: event.changed, etag: event.etag };
    case 'pollNotModified':
      return {};
    case 'pollNetworkError':
      return { message: event.message };
    case 'pollServerError':
      return { status: event.status };
    case 'pollParseError':
      return { message: event.message };
    case 'lkgLoaded':
      return { etag: event.etag };
  }
}
